import classNames from "classnames";
import React, { useRef } from "react";
import repeaterDivIcon from "../../assets/reapeater-div-placeholder.svg";
import { DynamicGridNode, NodeStates } from "../../build/types";
import useInfiniteScroll from "../../hooks/useInfiniteScroll";
import { useInteractionStates } from "../../hooks/useInteractionStates";
import { cloneNode } from "../../utils/cloneNode";
import { renderRepeaterDiv } from "../../utils/renderRepeaterDiv";
import { DynamicElement, dynamicGridGroupBy } from "../BuilderInterface";
import { getCompoundStyle, getStateStyleForBuilder, getStylesFromInteractiveStates } from "../helpers";
import "./style.scss";
import { ActiveCustomer } from "../../build/activeEntityType";

const GroupingSeperator = ({ groupName, dividerStyles }: { groupName: string; dividerStyles?: React.CSSProperties }) => {
    return (
        <span className="group-separator" style={dividerStyles}>
            {groupName}
        </span>
    );
};

const groupData = (dataSet: any[], groupBy: dynamicGridGroupBy | undefined) => {
    if (groupBy && groupBy !== "all") {
        const groups: { [key: string]: any[] } = {};

        if (groupBy === "orderDate" || groupBy === "updatedAt" || groupBy === "createdAt") {
            dataSet.forEach((item: any) => {
                const date = new Date(groupBy === "updatedAt" ? item.updatedAt : item.createdAt);
                const dateString = isNaN(date.getTime()) ? "" : date.toDateString();
                if (!groups[dateString]) {
                    groups[dateString] = [];
                }
                groups[dateString].push(item);
            });
        } else if (groupBy === "firstName" || groupBy === "lastName") {
            dataSet.forEach((item: ActiveCustomer) => {
                const name = groupBy === "firstName" ? item.firstName : item.lastName;
                const firstLetter = name.charAt(0).toUpperCase();
                if (!groups[firstLetter]) {
                    groups[firstLetter] = [];
                }
                groups[firstLetter].push(item);
            });
        }

        const keys = Object.keys(groups);
        if (keys.length === 0) {
            return [{ groupName: null, items: dataSet }];
        }
        return keys.map((dateString: string) => ({
            groupName: dateString,
            items: groups[dateString]
        }));
    }

    return [{ groupName: null, items: dataSet }];
};

const BuilderDynamicGrid = <T,>({
    node,
    builderProps,
    children,
    dynamicDataSet,
    renderProps,
    disabled,
    activeItemId,
    isLoading
}: DynamicElement<T>) => {
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const { attr, id, stateManager, groupBy, dividerStyles } = node as DynamicGridNode;

    // There should only be one child, but old builds may have an array. To support them:
    const repeaterDivChild = Array.isArray(children) ? children[0] : children;
    const emptyStateChild = Array.isArray(children) ? children[1] : children;

    const isInEmptyState = repeaterDivChild.props.node.name !== "repeaterDiv"; //the condition above should take care of this

    const isLoadingState = isLoading ?? false;

    const isEmptyData = !dynamicDataSet || dynamicDataSet.length === 0;
    /** Empty grid styling whenever we are not loading and have no rows (avoids blank UI when load never toggled isLoading). */
    const shouldShowEmptyState = !isLoadingState && isEmptyData;

    const productGridClass = classNames("dynamic-grid", {
        [`${attr?.className}`]: !!attr?.className,
        ["builder-mode"]: !!builderProps,
        ["empty-state"]: isInEmptyState || (!builderProps && shouldShowEmptyState)
    });

    const { setElement, states } = useInteractionStates<HTMLDivElement>();

    const { onIntersect, hasMore } = renderProps || { onIntersect: () => {}, hasMore: false };

    useInfiniteScroll({ sentinelRef, hasMore, onIntersect });

    const groupedData = groupData(dynamicDataSet, groupBy);
    const hasRenderableItems = groupedData.some(group => group.items.length > 0);

    if (builderProps) {
        const { nodeState, selectCallback, outlineCallback, onDragStart, onDragEnd } = builderProps;

        return (
            <div
                {...attr}
                className={productGridClass}
                style={getCompoundStyle({
                    node,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style")
                })}
                onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                id={id}
                onDragStart={e => onDragStart?.(e, id)}
                draggable={onDragStart ? "true" : "false"}
                onDragEnd={onDragEnd}
            >
                {isInEmptyState ? (
                    emptyStateChild
                ) : (
                    <>
                        {repeaterDivChild.props.node.name === "repeaterDiv" &&
                            (groupedData && dynamicDataSet && dynamicDataSet.length ? (
                                groupedData.map((group, groupIndex) => (
                                    <React.Fragment key={`group-${groupIndex}`}>
                                        {group.groupName && (
                                            <GroupingSeperator
                                                dividerStyles={dividerStyles}
                                                key={`${group.groupName}-${groupIndex}`}
                                                groupName={group.groupName}
                                            />
                                        )}
                                        {group.items.map((value: any, index: number) => {
                                            const itemIndex = groupIndex * 1000 + index;
                                            if (index === 0) {
                                                const firstDiv = cloneNode({
                                                    node: repeaterDivChild,
                                                    index: itemIndex,
                                                    dataObj: value,
                                                    keepFunctional: true
                                                });
                                                return firstDiv;
                                            } else if (repeaterDivChild.props.children?.length > 0) {
                                                const otherOnes = cloneNode({
                                                    node: repeaterDivChild,
                                                    index: itemIndex,
                                                    dataObj: value,
                                                    keepFunctional: false
                                                });
                                                return otherOnes;
                                            } else {
                                                return (
                                                    <div
                                                        className="repeater-placeholder builder-mode"
                                                        key={itemIndex}
                                                        style={{ height: repeaterDivChild.props.node.attr?.style?.height }}
                                                    >
                                                        <img src={repeaterDivIcon} alt="" />
                                                        <p>Repeater div</p>
                                                    </div>
                                                );
                                            }
                                        })}
                                    </React.Fragment>
                                ))
                            ) : (
                                <>
                                    {/* children besides empty state*/ children}
                                    <div
                                        className="repeater-placeholder"
                                        style={{ height: repeaterDivChild.props.node.attr?.style?.height }}
                                    >
                                        <img src={repeaterDivIcon} alt="" />
                                        <p>Repeater div</p>
                                    </div>
                                </>
                            ))}
                        <div ref={sentinelRef} style={{ height: "1px" }} />
                    </>
                )}
            </div>
        );
    } else {
        const dynamicGridStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: getCompoundStyle({ node }),
            stateManager,
            paths: ["attr_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const runtimeMain = isInEmptyState
            ? emptyStateChild
            : isLoadingState && isEmptyData
              ? null
              : isEmptyData || !hasRenderableItems
                ? emptyStateChild
                : renderProps
                  ? groupedData.map((group, groupIndex) => (
                        <React.Fragment key={`group-${groupIndex}`}>
                            {group.groupName && (
                                <GroupingSeperator
                                    dividerStyles={dividerStyles}
                                    key={`${group.groupName}-${groupIndex}`}
                                    groupName={group.groupName}
                                />
                            )}
                            {group.items.map((data: any, index: number) => {
                                group.groupName;
                                const repeaterNodeId = node.children![0];
                                const itemIndex = groupIndex * 1000 + index;
                                return renderRepeaterDiv({
                                    baseLayer: renderProps.layer,
                                    nodeKey: repeaterNodeId,
                                    dataObj: data,
                                    defaultCallback: renderProps.callback,
                                    index: itemIndex,
                                    actionCallbacks: renderProps.actionCallbacks,
                                    textSettings: renderProps.textSettings,
                                    activeItemId
                                });
                            })}
                        </React.Fragment>
                    ))
                  : emptyStateChild;

        return (
            <div {...attr} className={productGridClass} id={id} ref={setElement} style={dynamicGridStyle}>
                {runtimeMain}
                <div ref={sentinelRef} style={{ height: "1px" }} />
            </div>
        );
    }
};

export default BuilderDynamicGrid;
